import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Alert, Badge, Button, InlineLoader, Panel } from '../../components/ui'
import { fetchSettings, setOutbound } from '../../api/settings'
import { useSession } from '../../auth/session'
import { t } from '../../i18n'

/**
 * Whether anything may leave the account at all, and the button that changes it.
 *
 * This is the one setting that moves without anybody touching it. The sender
 * drops it when Telegram revokes the session, when a flood wait runs longer than
 * the configured pause, and when the day's send limit is reached — all three are
 * ordinary events for a personal account.
 *
 * Until this panel existed there was nowhere to put it back. The owner approved a
 * reply, the panel said it was queued, the job snoozed for a minute and came back
 * for ever, and nothing anywhere said why the client never answered.
 */
export function Outbound() {
  const { can } = useSession()
  const client = useQueryClient()
  const [reason, setReason] = useState('')
  const [asking, setAsking] = useState(false)

  const settings = useQuery({ queryKey: ['settings'], queryFn: ({ signal }) => fetchSettings(signal) })

  const move = useMutation({
    mutationFn: ({ enabled, why }: { enabled: boolean; why: string }) => setOutbound(enabled, why),
    onSuccess: () => {
      setAsking(false)
      setReason('')
      void client.invalidateQueries({ queryKey: ['settings'] })
    },
  })

  if (settings.isPending) {
    return (
      <Panel className="outbound">
        <InlineLoader />
      </Panel>
    )
  }
  if (settings.isError || settings.data === undefined) {
    return (
      <Panel className="outbound">
        <Alert tone="error">{t('outbound.unreadable')}</Alert>
      </Panel>
    )
  }

  const on = settings.data.outboundEnabled
  const editable = can('settings.write')

  return (
    <Panel className="outbound">
      <header className="outbound__head">
        <h2 className="outbound__title">{t('outbound.title')}</h2>
        <Badge tone={on ? 'accent' : 'danger'}>
          {on ? t('outbound.on') : t('outbound.off')}
        </Badge>
      </header>

      {/*
        Written here rather than taken from the server, which answers in English.
        The two facts that matter are who moved it and why, and both travel as
        data.
      */}
      <p className="outbound__explanation">
        {t(`outbound.by.${settings.data.outboundSource}` as 'outbound.by.person', {
          reason: settings.data.outboundReason === ''
            ? t('outbound.noReason')
            : settings.data.outboundReason,
        })}
      </p>

      {on ? null : (
        <Alert tone="warning">{t('outbound.whileOff')}</Alert>
      )}

      {move.isError ? <Alert tone="error">{t('outbound.failed')}</Alert> : null}

      {!editable ? null : on ? (
        asking ? (
          <div className="outbound__actions">
            {/*
              Switching off needs a reason and the server refuses without one, so
              the field is asked for before the button rather than after the
              rejection.
            */}
            <input
              className="field__input"
              value={reason}
              placeholder={t('outbound.reasonPlaceholder')}
              onChange={(event) => {
                setReason(event.target.value)
              }}
            />
            <Button
              variant="danger"
              busy={move.isPending}
              disabled={reason.trim() === ''}
              onClick={() => {
                move.mutate({ enabled: false, why: reason.trim() })
              }}
            >
              {t('outbound.turnOff')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setAsking(false)
                setReason('')
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        ) : (
          <div className="outbound__actions">
            <Button
              variant="secondary"
              onClick={() => {
                setAsking(true)
              }}
            >
              {t('outbound.turnOff')}
            </Button>
          </div>
        )
      ) : (
        <div className="outbound__actions">
          <Button
            busy={move.isPending}
            onClick={() => {
              // Every change to this setting carries a reason, turning it back
              // on included: the next person to open the screen reads why it
              // moved. Nobody should have to type a sentence to end an outage,
              // so the panel supplies the true one.
              move.mutate({ enabled: true, why: t('outbound.turnedOnReason') })
            }}
          >
            {t('outbound.turnOn')}
          </Button>
        </div>
      )}
    </Panel>
  )
}
